import { Box, Button, DialogActions, DialogContent, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import PickUpWithLimit from "src/components/shared/PickUpWithLimit";
import { COMMOM_STATUS, EXPORT_TYPE, orderStatusFormatted, PRINT_SIZE, RETURN_REFUND_OPTIONS, TRANSPORTATION_TYPE } from "src/constant";
import useAppGlobal from "src/context/useAppGlobal";
import useOrderCreate from "src/hooks/orders/useOrderCreate";
import useOrderDetail from "src/hooks/orders/useOrderDetail";
import useOrderPreview from "src/hooks/orders/useOrderPreview";
import useOrderPrint from "src/hooks/orders/useOrderPrint";
import useProcessLogDetail from "src/hooks/process-log/useProcessLogDetail";
import appConfig from "src/config/env";
import OrderReturnRefundTable from "./OrderReturnRefundTable";
import DeliveredReceipt from "./export-form/DeliveredReceipt";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormControlRadio from "src/components/shared/FormControlRadio";

/** Phiếu giao hàng / vận đơn (template ĐVVC, COD, QR — API preview `DeliveredReceipt`). */
const RETURN_REFUND_LABEL_OUTPUT = EXPORT_TYPE.DeliveredReceipt;

const option = [
  {
    value: RETURN_REFUND_OPTIONS.RETURN,
    label: "Trả hàng",
  },
  {
    value: RETURN_REFUND_OPTIONS.EXCHANGE,
    label: "Đổi hàng",
  },
];

const schema = yup.object().shape({
  type: yup.string().required("Vui lòng chọn hình thức"),
  prod: yup.array().of(
    yup.object().shape({
      qty: yup.number(),
      pickedQty: yup
        .number()
        .required("Vui lòng nhập số lượng")
        .min(1, "Số lượng phải lớn hơn 0")
        .test("pickedQty-less-than-qty", "Số lượng trả phải nhỏ hơn số lượng sản phẩm", function (value) {
          return value <= this.parent.qty;
        }),
    }),
  ),
  pickUpType: yup.string().required("Vui lòng chọn phương thức lấy hàng"),
  pickupDate: yup.string().required("Vui lòng chọn ngày lấy hàng"),
  note: yup.string().nullable().max(500, "Không nhập quá 500 ký tự"),
});

export default function OrderReturnRefundDialog({ item, mutate: parentMutate }) {
  const { closeDialog } = useAppGlobal();

  const [prodPick, setProdPick] = useState([]);
  /** Đơn đổi/trả vừa tạo — dùng preview / in vận đơn (giống PrepareOrderDialog trước khi sửa). */
  const [labelOrderId, setLabelOrderId] = useState(null);
  const [logId, setLogId] = useState();
  const { data: order } = useOrderDetail(item?._id, { warehouse: item?.warehouse });
  const { trigger: createOrder } = useOrderCreate();
  const { data: orderPreview, isLoading: previewLoading } = useOrderPreview(labelOrderId, {
    warehouse: item?.warehouse,
    outputFile: RETURN_REFUND_LABEL_OUTPUT,
  });
  const { mutate, isLoading: printLoading } = useProcessLogDetail(logId, {
    warehouse: item?.warehouse,
  });
  const { trigger: printAll } = useOrderPrint();

  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      type: RETURN_REFUND_OPTIONS.RETURN,
      prod: order?.data?.orderDetails,
      pickUpAddress: item?.pickUpAddress,
      pickUpType: TRANSPORTATION_TYPE.PICKUP,
    },
  });

  const { errors } = form.formState;
  const prodErrorMessage = errors?.prod?.find((rowError) => rowError?.pickedQty?.message)?.pickedQty?.message;

  const handleGetLog = (logIdParam) => {
    const logIdToUse = logIdParam || logId;
    if (!logIdToUse) return;
    const timeOut = setInterval(async () => {
      try {
        const res = await mutate();
        if (res?.success) {
          if (res.data.status === COMMOM_STATUS.done) {
            if (Array.isArray(res.data?.urls)) {
              res.data.urls.forEach((v) => {
                window.open(`${appConfig.API_FILE}/${v}`, "_blank");
              });
            }
            clearInterval(timeOut);
            closeDialog();
          }

          if (res.data.status === COMMOM_STATUS.error) {
            enqueueSnackbar("Không xuất được đơn", { variant: "error" });
            clearInterval(timeOut);
          }
        }
      } catch {
        clearInterval(timeOut);
      }
    }, 1000);
  };

  const handlePrintLabel = async () => {
    // if (!labelOrderId) return;
    const res = await printAll({
      body: {
        orderIds: [labelOrderId],
        pageSize: PRINT_SIZE.a6,
        warehouse: item?.warehouse,
        outputFiles: [RETURN_REFUND_LABEL_OUTPUT],
      },
    });
    if (res?.success) {
      setLogId(res?.data?._id);
      handleGetLog(res?.data?._id);
      return enqueueSnackbar("Tiến hành xuất phiếu...", { variant: "success", className: 'notistack-confirm' });
    }
    return enqueueSnackbar(res?.error?.message ?? "Lỗi khi in", { variant: "error" });
  };

  // SUBMIT FORM
  const handleSubmit = async (value) => {
    const totalOrder = order?.data?.orderDetails?.reduce((acc, curr) => acc + curr?.qty, 0);
    const totalOrderType = value.prod?.reduce((acc, curr) => acc + Number(curr?.qty), 0);

    if (totalOrderType > totalOrder) {
      enqueueSnackbar("Vượt quá số lượng sản phẩm của đơn", {
        variant: "warning",
      });
      return;
    }

    const returnItem = value?.prod?.map((v) => ({ qty: v.pickedQty, productModel: v.productModel._id, discount: v.discount, defaultQty: v.qty }));

    for (const item of returnItem) {
      if (item.qty > item.defaultQty) {
        enqueueSnackbar("Số lượng sản phẩm trả vượt quá số lượng sản phẩm của đơn", {
          variant: "error",
        });
        return;
      }
    }

    const res = await createOrder({
      body: {
        ...value,
        status: value?.type === RETURN_REFUND_OPTIONS.EXCHANGE ? orderStatusFormatted.WaitToExport : orderStatusFormatted.Returning,
        pickUpAddress: item?.pickUpAddress,
        warehouse: item?.warehouse,
        rootOrder: item?._id,
        customer: item?.customer?._id,
        sales: item?.sales?._id,
        shippingProvider: item?.shippingProvider?._id,
        recipient: item?.recipient,
        paymentMethod: item?.paymentMethod?._id,
        shippingFee: 0,
        cod: 0,
        // type: order?.type,
        orderDetails: [...returnItem],
      },
    });
    if (res?.success) {
      const newId = res?.data?._id ?? res?.data?.id;
      if (newId) {
        setLabelOrderId(newId);
        enqueueSnackbar("Thành công! Vui lòng in phiếu giao hàng.", { variant: "success" });
        return;
      }
      return;
    }
    enqueueSnackbar(res?.error?.message ?? "Lỗi khi tạo đơn hàng đổi trả", { variant: "error" });
  };

  useEffect(() => {
    form.reset({
      type: RETURN_REFUND_OPTIONS.RETURN,
      prod: order?.data?.orderDetails,
      pickUpAddress: item?.pickUpAddress,
      pickUpType: TRANSPORTATION_TYPE.PICKUP,
      pickupDate: order?.data?.pickupDate,
    });
  }, [item?.pickUpAddress, order?.data?.orderDetails, order?.data?.pickupDate]);

  return (
    <FormProvider {...form}>
      <DialogContent>
        {!labelOrderId ? (
          <>
            {/* TYPE */}
            <FormControlRadio
              name="type"
              label="Hình thức"
              options={option}
              required
              sx={{ display: "flex", flexDirection: "row", gap: "12px" }}
            />
            {/* INFOMATION */}
            <Box sx={{ background: "var(--grey)", borderRadius: "6px", p: "12px" }}>
              <Typography
                fontWeight="bold"
                fontSize="16px"
              >
                Thông tin Khách hàng
              </Typography>
              <Typography>{item?.customer?.fullName ?? "-"}</Typography>
              <Typography>{item?.customer?.email ?? "-"}</Typography>
              <Typography>{item?.customer?.phone ?? "-"}</Typography>
              <Typography>{item?.recipient?.detailAddress ?? "-"}</Typography>
            </Box>
            {/* PROD LIST */}
            <Typography
              fontWeight="bold"
              m="24px 0 16px"
            >
              Danh sách sản phẩm
            </Typography>

            <OrderReturnRefundTable
              prod={order?.data}
              setProdPick={setProdPick}
              prodPick={prodPick}
            />
            {!!prodErrorMessage && (
              <Typography
                color="error"
                mt="6px"
                fontSize="12px"
              >
                {prodErrorMessage}
              </Typography>
            )}

            {/* TRANSPORTATION INFO */}
            <Box mt="24px">
              <PickUpWithLimit item={item} />
            </Box>
          </>
        ) : (
          <Box>
            <Typography
              fontWeight="bold"
              fontSize="16px"
              mb={2}
            >
              Phiếu giao hàng
            </Typography>
            <Box
              sx={{
                maxWidth: RETURN_REFUND_LABEL_OUTPUT === EXPORT_TYPE.DeliveredReceipt ? PRINT_SIZE.width : "100%",
                maxHeight: RETURN_REFUND_LABEL_OUTPUT === EXPORT_TYPE.DeliveredReceipt ? PRINT_SIZE.height : "100%",
                width: RETURN_REFUND_LABEL_OUTPUT === EXPORT_TYPE.DeliveredReceipt ? "90%" : "100%",
                m: "0 auto",
              }}
            >
              <DeliveredReceipt
                isLoading={previewLoading}
                html={orderPreview}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: "column", gap: 1, px: 3, pb: 2 }}>
        {!labelOrderId ? (
          <Button
            onClick={form.handleSubmit(handleSubmit)}
            fullWidth
            variant="contained"
          >
            Xác nhận
          </Button>
        ) : (
          <>
            <Button
              onClick={handlePrintLabel}
              disabled={printLoading}
              fullWidth
              variant="contained"
            >
              In phiếu giao hàng
            </Button>
          </>
        )}
      </DialogActions>
    </FormProvider>
  );
}
